'use client';

import { useRef, useState } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { usePostHog } from 'posthog-js/react';
import { SectionHeading } from '@/components/shared/SectionHeading';
import { Button } from '@/components/shared/Button';
import { fadeInUp, staggerContainer } from '@/lib/animations';
import { addUTMs } from '@/lib/utils';
import { cn } from '@/lib/utils';
import type { PricingContent, GlobalContent } from '@/lib/content-schema';

interface Props {
  content: PricingContent;
  global: GlobalContent;
}

let _dashboardUrl = 'https://dashboard.zibarit.com';

const COLORS = {
  verdeBrillante: '#D2DC7C',
  verdeOscuro: '#325443',
  verdeSecundario: '#919F64',
  gris: '#8492a6',
};

/* ------------------------------------------------------------------ */
/*  Data                                                               */
/* ------------------------------------------------------------------ */

const plans = [
  {
    name: 'Starter',
    tagline: 'Para empezar sin compromiso',
    audience: 'Organizadores ocasionales o que quieren probar la plataforma.',
    monthlyFee: null,
    annualMonthlyFee: null,
    platformFee: '4%',
    paymentFee: '2%',
    totalLabel: '6% en reservas directas',
    accent: COLORS.verdeSecundario,
    features: [
      'Sin coste mensual',
      'Gestión de eventos y entradas',
      'Control de reservas y check-in',
      'Códigos promocionales',
      'Panel de ventas y facturación',
      'Soporte básico',
      'Comisión 12% en reservas Zibarit Club (alcance extra)',
    ],
    limitations: [
      'Sin acceso a estadísticas',
      'No se permiten eventos gratuitos',
      'Se cobran cancelaciones',
    ],
    highlighted: false,
  },
  {
    name: 'Pro',
    tagline: 'Crece con la comunidad',
    audience: 'Para organizadores recurrentes que quieren más visibilidad.',
    monthlyFee: '59€',
    annualMonthlyFee: '47€',
    platformFee: '0%',
    paymentFee: '2,5%',
    totalLabel: '2,5% en todas las reservas',
    accent: COLORS.verdeOscuro,
    features: [
      'Todo lo de Starter',
      'Sin coste de cancelaciones',
      'Eventos gratuitos permitidos',
      'Bienvenida en redes sociales',
      '1 story promocional por evento',
      'Comisión única en todas las reservas',
      'Acceso completo a estadísticas',
    ],
    limitations: [],
    highlighted: true,
  },
  {
    name: 'Growth',
    tagline: 'Máxima visibilidad',
    audience: 'Para quienes buscan el máximo impulso dentro de la comunidad Zibarit.',
    monthlyFee: '149€',
    annualMonthlyFee: '119€',
    platformFee: '0%',
    paymentFee: '2%',
    totalLabel: '2% en todas las reservas',
    accent: COLORS.verdeBrillante,
    features: [
      'Todo lo de Pro',
      'Comisión única en todas las reservas',
      '2 stories por evento',
      '1 post mensual recap de eventos',
      'Difusión en WhatsApp general',
      'Prioridad en difusión de eventos',
      'Mayor visibilidad en plataforma',
      'Acceso prioritario a campañas',
      'Promoción destacada en seleccionados',
      'Motor de reservas integrable en tu propia web',
      'Perfil avanzado del Organizador integrable en tu propia web',
    ],
    limitations: [],
    highlighted: false,
  },
];

const competitors = [
  {
    name: 'Eventbrite',
    platformFee: '3,7% + 1,79€',
    paymentFee: '~2,9%',
    totalRange: '7% – 10%',
    monthlyFee: '—',
    community: false,
    sectorFocus: false,
    promotion: false,
    freeEvents: true,
  },
  {
    name: 'Luma',
    platformFee: '5%',
    paymentFee: '2,9% + 0,30€',
    totalRange: '7% – 8%',
    monthlyFee: '—',
    community: false,
    sectorFocus: false,
    promotion: false,
    freeEvents: true,
  },
  {
    name: 'Luma Plus',
    platformFee: '0%',
    paymentFee: '2,9% + 0,30€',
    totalRange: '~3%',
    monthlyFee: '59$',
    community: false,
    sectorFocus: false,
    promotion: false,
    freeEvents: true,
  },
];

const zibaritAdvantages = [
  {
    icon: CommunityIcon,
    title: 'Comunidad real',
    description: 'Acceso directo a miles de entusiastas de gastronomía y bienestar segmentados por ciudad e intereses.',
  },
  {
    icon: SectorIcon,
    title: 'Enfoque sectorial',
    description: 'No compites con conciertos o conferencias. Tu evento se muestra al público que realmente te interesa.',
  },
  {
    icon: MegaphoneIcon,
    title: 'Promoción incluida',
    description: 'Stories, posts, WhatsApp broadcasts y embajadores Z. No es solo ticketing — es un sistema de difusión que llena tus eventos.',
  },
  {
    icon: ShieldIcon,
    title: 'Comisiones justas',
    description: 'Desde un 2% por ticket. Sin fees ocultos. Sin sorpresas. El modelo más transparente del mercado.',
  },
];

/* ------------------------------------------------------------------ */
/*  Comparison row types                                               */
/* ------------------------------------------------------------------ */

interface ComparisonRowFull {
  label: string;
  zibaritStarter: string;
  zibaritPro: string;
  eventbrite: string;
  luma: string;
  lumaPlus: string;
}

const comparisonRows: ComparisonRowFull[] = [
  {
    label: 'Coste mensual',
    zibaritStarter: '0€',
    zibaritPro: '59€',
    eventbrite: '0€',
    luma: '0€',
    lumaPlus: '59$',
  },
  {
    label: 'Comisión total por ticket',
    zibaritStarter: '6 – 12%',
    zibaritPro: '2,5%',
    eventbrite: '7 – 10%',
    luma: '7 – 8%',
    lumaPlus: '~3%',
  },
  {
    label: 'Comunidad sectorial',
    zibaritStarter: 'check',
    zibaritPro: 'check',
    eventbrite: 'cross',
    luma: 'cross',
    lumaPlus: 'cross',
  },
  {
    label: 'Promoción en RRSS incluida',
    zibaritStarter: 'cross',
    zibaritPro: 'check',
    eventbrite: 'cross',
    luma: 'cross',
    lumaPlus: 'cross',
  },
  {
    label: 'Difusión WhatsApp',
    zibaritStarter: 'cross',
    zibaritPro: 'check',
    eventbrite: 'cross',
    luma: 'cross',
    lumaPlus: 'cross',
  },
  {
    label: 'Eventos gratuitos',
    zibaritStarter: 'cross',
    zibaritPro: 'check',
    eventbrite: 'check',
    luma: 'check',
    lumaPlus: 'check',
  },
  {
    label: 'Público food & wellness',
    zibaritStarter: 'check',
    zibaritPro: 'check',
    eventbrite: 'cross',
    luma: 'cross',
    lumaPlus: 'cross',
  },
  {
    label: 'Acompañamiento estratégico',
    zibaritStarter: 'cross',
    zibaritPro: 'check',
    eventbrite: 'cross',
    luma: 'cross',
    lumaPlus: 'cross',
  },
];

/* ------------------------------------------------------------------ */
/*  Main component                                                     */
/* ------------------------------------------------------------------ */

export function PricingSection({ content: c, global: g }: Props) {
  _dashboardUrl = g.dashboardUrl;
  return (
    <div id="precios">
      <DiffusionPricing content={c} global={g} />
      <TicketingPricing content={c} />
      <ComparisonBlock />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Diffusion Pricing                                                  */
/* ------------------------------------------------------------------ */

function DiffusionPricing({ content: c, global: g }: { content: PricingContent; global: GlobalContent }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });
  const posthog = usePostHog();
  const channelIcons = [WhatsAppIcon, InstagramIcon, FacebookIcon, AmbassadorIcon];

  return (
    <section className="py-24 md:py-32 bg-zibarit-bone relative overflow-hidden">
      <div className="absolute top-0 left-0 w-[400px] h-[400px] rounded-full bg-zibarit-verde-oscuro/[0.03] blur-[120px] -translate-y-1/2 -translate-x-1/3" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <SectionHeading
          subtitle={c.diffusion.sectionSubtitle}
          title={c.diffusion.sectionTitle}
          description={c.diffusion.sectionDescription}
        />

        <motion.div
          ref={ref}
          variants={staggerContainer}
          initial="initial"
          animate={isInView ? 'animate' : 'initial'}
          className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-12"
        >
          {c.diffusion.channels.map((channel, index) => (
            <motion.div
              key={channel.name}
              variants={fadeInUp}
              className="bg-white rounded-2xl p-6 md:p-7 shadow-sm hover:shadow-md transition-shadow duration-300"
            >
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-xl bg-zibarit-verde-oscuro/10 flex items-center justify-center shrink-0">
                  {channelIcons[index]()}
                </div>
                <div>
                  <h4 className="text-subtitle text-sm text-zibarit-negro mb-1.5">
                    {channel.name}
                  </h4>
                  <p className="text-body text-sm text-zibarit-marron-oscuro leading-relaxed">
                    {channel.description}
                  </p>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Pricing model card */}
        <motion.div
          variants={fadeInUp}
          initial="initial"
          animate={isInView ? 'animate' : 'initial'}
          className="max-w-2xl mx-auto"
        >
          <div className="bg-zibarit-negro rounded-2xl p-8 md:p-10 text-center">
            <span
              className="inline-block px-4 py-1.5 rounded-full text-xs font-lato font-bold uppercase tracking-[0.15em] text-zibarit-negro mb-6"
              style={{ backgroundColor: COLORS.verdeBrillante }}
            >
              {c.diffusion.modelLabel}
            </span>
            <ul className="space-y-3 text-left max-w-md mx-auto mb-8">
              {c.diffusion.modelFeatures.map((item) => (
                <li key={item} className="flex items-start gap-3">
                  <div className="w-5 h-5 rounded-full bg-zibarit-verde-oscuro/30 flex items-center justify-center shrink-0 mt-0.5">
                    <svg className="w-3 h-3 text-zibarit-verde-brillante" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <span className="text-body text-sm text-zibarit-bone/90 leading-snug">{item}</span>
                </li>
              ))}
            </ul>
            <p className="text-body text-sm text-zibarit-marron-secundario mb-8">
              {c.diffusion.modelFootnote}
            </p>
            <Button
              href={addUTMs(_dashboardUrl + '/register', 'pro_diffusion_pricing')}
              target="_blank"
              rel="noopener noreferrer"
              magnetic
              size="lg"
              data-ph-capture
              className="bg-zibarit-verde-brillante text-zibarit-negro hover:bg-zibarit-verde-secundario"
              onClick={() => posthog?.capture('landing_cta_clicked', {
                button_text: 'Solicitar presupuesto',
                destination_url: addUTMs(_dashboardUrl + '/register', 'pro_diffusion_pricing'),
                page: 'pro',
              })}
            >
              {c.diffusion.modelButtonText}
            </Button>
            <p className="text-body text-xs text-zibarit-marron-secundario/60 mt-4">
              {c.diffusion.modelSubFootnote}
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function WhatsAppIcon() {
  return (
    <svg className="w-5 h-5 text-zibarit-verde-oscuro" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M8.625 12a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H8.25m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H12m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0h-.375M21 12c0 4.556-4.03 8.25-9 8.25a9.764 9.764 0 01-2.555-.337A5.972 5.972 0 015.41 20.97a5.969 5.969 0 01-.474-.065 4.48 4.48 0 00.978-2.025c.09-.457-.133-.901-.467-1.226C3.93 16.178 3 14.189 3 12c0-4.556 4.03-8.25 9-8.25s9 3.694 9 8.25z" />
    </svg>
  );
}

function InstagramIcon() {
  return (
    <svg className="w-5 h-5 text-zibarit-verde-oscuro" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M6.827 6.175A2.31 2.31 0 015.186 7.23c-.38.054-.757.112-1.134.175C2.999 7.58 2.25 8.507 2.25 9.574V18a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9.574c0-1.067-.75-1.994-1.802-2.169a47.865 47.865 0 00-1.134-.175 2.31 2.31 0 01-1.64-1.055l-.822-1.316a2.192 2.192 0 00-1.736-1.039 48.774 48.774 0 00-5.232 0 2.192 2.192 0 00-1.736 1.039l-.821 1.316z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 12.75a4.5 4.5 0 11-9 0 4.5 4.5 0 019 0z" />
    </svg>
  );
}

function FacebookIcon() {
  return (
    <svg className="w-5 h-5 text-zibarit-verde-oscuro" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582m15.686 0A11.953 11.953 0 0112 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0121 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0112 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 013 12c0-1.605.42-3.113 1.157-4.418" />
    </svg>
  );
}

function AmbassadorIcon() {
  return (
    <svg className="w-5 h-5 text-zibarit-verde-oscuro" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M10.34 15.84c-.688-.06-1.386-.09-2.09-.09H7.5a4.5 4.5 0 110-9h.75c.704 0 1.402-.03 2.09-.09m0 9.18c.253.962.584 1.892.985 2.783.247.55.06 1.21-.463 1.511l-.657.38c-.551.318-1.26.117-1.527-.461a20.845 20.845 0 01-1.44-4.282m3.102.069a18.03 18.03 0 01-.59-4.59c0-1.586.205-3.124.59-4.59m0 9.18a23.848 23.848 0 018.835 2.535M10.34 6.66a23.847 23.847 0 008.835-2.535m0 0A23.74 23.74 0 0018.795 3m.38 1.125a23.91 23.91 0 011.014 5.395m-1.014 8.855c-.118.38-.245.754-.38 1.125m.38-1.125a23.91 23.91 0 001.014-5.395m0-3.46c.495.413.811 1.035.811 1.73 0 .695-.316 1.317-.811 1.73m0-3.46a24.347 24.347 0 010 3.46" />
    </svg>
  );
}

/* ------------------------------------------------------------------ */
/*  Pricing Cards                                                      */
/* ------------------------------------------------------------------ */

type Billing = 'monthly' | 'annual';

function BillingToggle({ billing, onChange }: { billing: Billing; onChange: (b: Billing) => void }) {
  const isAnnual = billing === 'annual';
  return (
    <div className="flex items-center justify-center gap-4 mb-12">
      <button
        onClick={() => onChange('monthly')}
        className={cn(
          'text-body text-sm transition-colors duration-200',
          !isAnnual ? 'text-zibarit-negro font-semibold' : 'text-zibarit-marron-oscuro/50',
        )}
      >
        Mensual
      </button>

      <button
        onClick={() => onChange(isAnnual ? 'monthly' : 'annual')}
        className={cn(
          'relative w-12 h-6 rounded-full transition-colors duration-300 focus:outline-none',
          isAnnual ? 'bg-zibarit-verde-oscuro' : 'bg-zibarit-marron-oscuro/20',
        )}
        aria-label="Cambiar facturación"
      >
        <motion.span
          layout
          transition={{ type: 'spring', stiffness: 500, damping: 35 }}
          className="absolute top-0.5 w-5 h-5 rounded-full bg-white shadow-sm"
          style={{ left: isAnnual ? 'calc(100% - 1.375rem)' : '0.125rem' }}
        />
      </button>

      <button
        onClick={() => onChange('annual')}
        className={cn(
          'flex items-center gap-2 text-body text-sm transition-colors duration-200',
          isAnnual ? 'text-zibarit-negro font-semibold' : 'text-zibarit-marron-oscuro/50',
        )}
      >
        Anual
        <AnimatePresence>
          {isAnnual ? (
            <motion.span
              key="badge"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              transition={{ duration: 0.2 }}
              className="inline-block px-2 py-0.5 rounded-full text-xs font-lato font-bold text-zibarit-negro"
              style={{ backgroundColor: COLORS.verdeBrillante }}
            >
              −20%
            </motion.span>
          ) : (
            <span
              className="inline-block px-2 py-0.5 rounded-full text-xs font-lato font-bold text-zibarit-negro/40 bg-zibarit-marron-oscuro/10"
            >
              −20%
            </span>
          )}
        </AnimatePresence>
      </button>
    </div>
  );
}

function TicketingPricing({ content: c }: { content: PricingContent }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });
  const [billing, setBilling] = useState<Billing>('monthly');
  const plans = c.ticketing.plans;

  return (
    <section className="py-24 md:py-32 bg-zibarit-bone relative overflow-hidden">
      {/* Subtle decorative elements */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full bg-zibarit-verde-oscuro/[0.03] blur-[120px] -translate-y-1/2 translate-x-1/3" />
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full bg-zibarit-verde-brillante/[0.04] blur-[100px] translate-y-1/3 -translate-x-1/4" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <SectionHeading
          subtitle={c.ticketing.sectionSubtitle}
          title={c.ticketing.sectionTitle}
          description={c.ticketing.sectionDescription}
        />

        <BillingToggle billing={billing} onChange={setBilling} />

        <motion.div
          ref={ref}
          variants={staggerContainer}
          initial="initial"
          animate={isInView ? 'animate' : 'initial'}
          className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-5 items-stretch"
        >
          {plans.map((plan) => (
            <PricingCard key={plan.name} plan={plan} isInView={isInView} billing={billing} />
          ))}
        </motion.div>

        {/* Trust note */}
        <motion.p
          variants={fadeInUp}
          initial="initial"
          animate={isInView ? 'animate' : 'initial'}
          className="text-body text-sm text-zibarit-marron-oscuro/70 text-center max-w-xl mx-auto mt-12 leading-relaxed"
        >
          {c.ticketing.trustNote.split('\n').map((line, i) => (
            <span key={i}>{line}{i < c.ticketing.trustNote.split('\n').length - 1 && <br />}</span>
          ))}
        </motion.p>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/*  Single pricing card                                                */
/* ------------------------------------------------------------------ */

interface PlanData {
  name: string;
  tagline: string;
  audience: string;
  monthlyFee: string | null;
  annualMonthlyFee: string | null;
  platformFee: string;
  paymentFee: string;
  totalLabel: string;
  accent: string;
  features: string[];
  limitations: string[];
  highlighted: boolean;
}

function PricingCard({ plan, isInView, billing }: { plan: PlanData; isInView: boolean; billing: Billing }) {
  const [isHovered, setIsHovered] = useState(false);
  const posthog = usePostHog();
  const isAnnual = billing === 'annual';
  const displayFee = isAnnual && plan.annualMonthlyFee ? plan.annualMonthlyFee : plan.monthlyFee;

  return (
    <motion.div
      variants={fadeInUp}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={cn(
        'relative rounded-2xl transition-all duration-500 group h-full flex flex-col',
        plan.highlighted
          ? 'bg-zibarit-negro text-zibarit-bone'
          : 'bg-white text-zibarit-negro',
      )}
      style={{
        boxShadow: plan.highlighted
          ? '0 25px 60px -12px rgba(50, 84, 67, 0.3), 0 0 0 1px rgba(210, 220, 124, 0.15)'
          : isHovered
            ? '0 20px 40px -12px rgba(0,0,0,0.1)'
            : '0 4px 20px -4px rgba(0,0,0,0.06)',
      }}
    >
      {/* Popular badge */}
      {plan.highlighted && (
        <div className="absolute -top-4 left-1/2 -translate-x-1/2 z-10">
          <span
            className="inline-block px-5 py-1.5 text-xs font-lato font-bold uppercase tracking-[0.15em] rounded-full text-zibarit-negro"
            style={{ backgroundColor: COLORS.verdeBrillante }}
          >
            Más popular
          </span>
        </div>
      )}

      {/* Top accent line */}
      <div
        className="h-1 rounded-t-2xl"
        style={{ backgroundColor: plan.accent }}
      />

      <div className="p-7 md:p-8 flex flex-col flex-1">
        {/* Header */}
        <div className="mb-6">
          <p
            className="text-subtitle text-xs mb-1"
            style={{ color: plan.accent }}
          >
            {plan.name}
          </p>
          <h3
            className={cn(
              'text-title text-2xl md:text-3xl mb-2',
              plan.highlighted ? 'text-zibarit-bone' : 'text-zibarit-negro',
            )}
          >
            {plan.tagline}
          </h3>
          <p
            className={cn(
              'text-body text-sm leading-relaxed',
              plan.highlighted ? 'text-zibarit-marron-secundario' : 'text-zibarit-marron-oscuro',
            )}
          >
            {plan.audience}
          </p>
        </div>

        {/* Price */}
        <div className="mb-6 pb-6 border-b border-current/10">
          {displayFee ? (
            <div>
              <div className="flex items-baseline gap-1">
                <AnimatePresence mode="wait">
                  <motion.span
                    key={displayFee}
                    initial={{ opacity: 0, y: -8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 8 }}
                    transition={{ duration: 0.2 }}
                    className="font-playfair text-4xl md:text-5xl font-bold tracking-tight"
                  >
                    {displayFee}
                  </motion.span>
                </AnimatePresence>
                <span
                  className={cn(
                    'text-body text-sm',
                    plan.highlighted ? 'text-zibarit-marron-secundario' : 'text-zibarit-marron-oscuro',
                  )}
                >
                  /mes
                </span>
              </div>
              {isAnnual && plan.annualMonthlyFee && (
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className={cn(
                    'text-body text-xs mt-1',
                    plan.highlighted ? 'text-zibarit-marron-secundario/60' : 'text-zibarit-marron-oscuro/50',
                  )}
                >
                  Facturado anualmente · antes {plan.monthlyFee}/mes
                </motion.p>
              )}
            </div>
          ) : (
            <div className="flex items-baseline gap-1">
              <span className="font-playfair text-4xl md:text-5xl font-bold tracking-tight">
                0€
              </span>
              <span
                className={cn(
                  'text-body text-sm',
                  plan.highlighted ? 'text-zibarit-marron-secundario' : 'text-zibarit-marron-oscuro',
                )}
              >
                /mes
              </span>
            </div>
          )}

          <div className="mt-3 flex items-center gap-3">
            <span
              className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-lato font-bold"
              style={{
                backgroundColor: `${plan.accent}18`,
                color: plan.highlighted ? plan.accent : plan.accent,
              }}
            >
              <span className="font-mono text-xs opacity-70">comisión</span>
              {plan.totalLabel}
            </span>
          </div>
        </div>

        {/* Features */}
        <ul className="space-y-3 mb-6 flex-1">
          {plan.features.map((feature) => (
            <li key={feature} className="flex items-start gap-3">
              <CheckIcon color={plan.accent} />
              <span
                className={cn(
                  'text-body text-sm leading-snug',
                  plan.highlighted ? 'text-zibarit-bone/90' : 'text-zibarit-negro/80',
                )}
              >
                {feature}
              </span>
            </li>
          ))}
          {plan.limitations.map((limitation) => (
            <li key={limitation} className="flex items-start gap-3 opacity-50">
              <CrossIcon />
              <span className="text-body text-sm leading-snug line-through decoration-1">
                {limitation}
              </span>
            </li>
          ))}
        </ul>

        {/* CTA */}
        <Button
          href={addUTMs(_dashboardUrl + '/register', `pro_pricing_${plan.name.toLowerCase()}`)}
          target="_blank"
          rel="noopener noreferrer"
          variant={plan.highlighted ? 'primary' : 'secondary'}
          size="md"
          data-ph-capture
          className={cn(
            'w-full justify-center',
            plan.highlighted && 'bg-zibarit-verde-brillante text-zibarit-negro hover:bg-zibarit-verde-secundario',
          )}
          onClick={() => posthog?.capture('landing_cta_clicked', {
            button_text: plan.highlighted ? 'EMPEZAR AHORA' : 'CREAR CUENTA',
            destination_url: addUTMs(_dashboardUrl + '/register', `pro_pricing_${plan.name.toLowerCase()}`),
            page: 'pro',
            plan_name: plan.name,
          })}
        >
          {plan.highlighted ? 'EMPEZAR AHORA' : 'CREAR CUENTA'}
        </Button>

      </div>
    </motion.div>
  );
}

/* ------------------------------------------------------------------ */
/*  Comparison with competitors                                        */
/* ------------------------------------------------------------------ */

function ComparisonBlock() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });
  const posthog = usePostHog();

  return (
    <section className="py-24 md:py-32 bg-zibarit-negro relative overflow-hidden">
      {/* Background texture */}
      <div className="absolute inset-0 opacity-[0.015]" style={{
        backgroundImage: `radial-gradient(circle at 1px 1px, white 1px, transparent 0)`,
        backgroundSize: '32px 32px',
      }} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <SectionHeading
          subtitle="Comparativa"
          title="No todas las plataformas son iguales."
          description="Otras plataformas solo venden entradas. Zibarit combina ticketing con difusión activa: comunidad real de food & wellness, promoción en redes y WhatsApp, y las comisiones más justas del mercado."
          dark
        />

        {/* Desktop comparison table */}
        <motion.div
          ref={ref}
          variants={fadeInUp}
          initial="initial"
          animate={isInView ? 'animate' : 'initial'}
          className="hidden md:block"
        >
          <div className="rounded-2xl overflow-hidden border border-white/10">
            {/* Header row */}
            <div className="grid grid-cols-6 bg-white/[0.04]">
              <div className="p-5 border-r border-white/5" />
              <div className="p-5 border-r border-white/5 text-center">
                <span className="text-subtitle text-xs text-zibarit-verde-brillante block mb-1">Zibarit</span>
                <span className="text-title text-lg text-zibarit-bone">Starter</span>
              </div>
              <div className="p-5 border-r border-white/5 text-center relative">
                <div className="absolute inset-0 bg-zibarit-verde-oscuro/20" />
                <div className="relative">
                  <span className="text-subtitle text-xs text-zibarit-verde-brillante block mb-1">Zibarit</span>
                  <span className="text-title text-lg text-zibarit-bone">Pro</span>
                </div>
              </div>
              <div className="p-5 border-r border-white/5 text-center">
                <span className="text-subtitle text-xs text-zibarit-gris block mb-1">&nbsp;</span>
                <span className="text-title text-lg text-zibarit-gris">Eventbrite</span>
              </div>
              <div className="p-5 border-r border-white/5 text-center">
                <span className="text-subtitle text-xs text-zibarit-gris block mb-1">&nbsp;</span>
                <span className="text-title text-lg text-zibarit-gris">Luma</span>
              </div>
              <div className="p-5 text-center">
                <span className="text-subtitle text-xs text-zibarit-gris block mb-1">&nbsp;</span>
                <span className="text-title text-lg text-zibarit-gris">Luma Plus</span>
              </div>
            </div>

            {/* Data rows */}
            {comparisonRows.map((row, index) => (
              <div
                key={row.label}
                className={cn(
                  'grid grid-cols-6',
                  index % 2 === 0 ? 'bg-white/[0.02]' : 'bg-transparent',
                )}
              >
                <div className="p-4 px-5 border-r border-white/5 flex items-center">
                  <span className="text-body text-sm text-zibarit-marron-secundario">
                    {row.label}
                  </span>
                </div>
                <ComparisonCell value={row.zibaritStarter} isZibarit />
                <ComparisonCell value={row.zibaritPro} isZibarit isHighlighted />
                <ComparisonCell value={row.eventbrite} />
                <ComparisonCell value={row.luma} />
                <ComparisonCell value={row.lumaPlus} isLast />
              </div>
            ))}
          </div>
        </motion.div>

        {/* Mobile comparison cards */}
        <motion.div
          variants={staggerContainer}
          initial="initial"
          animate={isInView ? 'animate' : 'initial'}
          className="md:hidden space-y-4"
        >
          <MobileComparisonCard
            title="Zibarit Starter"
            accent={COLORS.verdeSecundario}
            data={{
              'Coste mensual': '0€',
              'Comisión total': '6 – 12%',
              'Comunidad sectorial': 'check',
              'Promoción RRSS': 'cross',
              'Difusión WhatsApp': 'cross',
              'Público food & wellness': 'check',
            }}
          />
          <MobileComparisonCard
            title="Zibarit Pro"
            accent={COLORS.verdeOscuro}
            highlighted
            data={{
              'Coste mensual': '59€',
              'Comisión total': '2,5%',
              'Comunidad sectorial': 'check',
              'Promoción RRSS': 'check',
              'Difusión WhatsApp': 'check',
              'Público food & wellness': 'check',
            }}
          />
          <MobileComparisonCard
            title="Eventbrite"
            accent={COLORS.gris}
            data={{
              'Coste mensual': '0€',
              'Comisión total': '7 – 10%',
              'Comunidad sectorial': 'cross',
              'Promoción RRSS': 'cross',
              'Difusión WhatsApp': 'cross',
              'Público food & wellness': 'cross',
            }}
          />
          <MobileComparisonCard
            title="Luma"
            accent={COLORS.gris}
            data={{
              'Coste mensual': '0€',
              'Comisión total': '7 – 8%',
              'Comunidad sectorial': 'cross',
              'Promoción RRSS': 'cross',
              'Difusión WhatsApp': 'cross',
              'Público food & wellness': 'cross',
            }}
          />
          <MobileComparisonCard
            title="Luma Plus"
            accent={COLORS.gris}
            data={{
              'Coste mensual': '59$',
              'Comisión total': '~3%',
              'Comunidad sectorial': 'cross',
              'Promoción RRSS': 'cross',
              'Difusión WhatsApp': 'cross',
              'Público food & wellness': 'cross',
            }}
          />
        </motion.div>

        {/* Advantages grid */}
        <motion.div
          variants={staggerContainer}
          initial="initial"
          animate={isInView ? 'animate' : 'initial'}
          className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-16"
        >
          {zibaritAdvantages.map((adv) => (
            <motion.div
              key={adv.title}
              variants={fadeInUp}
              className="relative bg-white/[0.04] border border-white/[0.06] rounded-2xl p-6 md:p-7 hover:bg-white/[0.07] transition-colors duration-300 group"
            >
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-xl bg-zibarit-verde-oscuro/30 flex items-center justify-center shrink-0 group-hover:bg-zibarit-verde-oscuro/50 transition-colors">
                  <adv.icon />
                </div>
                <div>
                  <h4 className="text-subtitle text-sm text-zibarit-bone mb-1.5">
                    {adv.title}
                  </h4>
                  <p className="text-body text-sm text-zibarit-marron-secundario leading-relaxed">
                    {adv.description}
                  </p>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Bottom emphasis */}
        <motion.div
          variants={fadeInUp}
          initial="initial"
          animate={isInView ? 'animate' : 'initial'}
          className="mt-16 text-center"
        >
          <p className="text-title text-xl md:text-2xl text-zibarit-bone/90 max-w-2xl mx-auto mb-2">
            No vendes entradas.{' '}
            <span className="text-title-accent" style={{ color: COLORS.verdeBrillante }}>
              Llenas experiencias.
            </span>
          </p>
          <p className="text-body text-sm text-zibarit-marron-secundario max-w-lg mx-auto mt-4 mb-8">
            Con Zibarit no solo tienes ticketing — tienes difusión activa a miles de personas
            del sector food & wellness buscando exactamente lo que tú ofreces.
          </p>
          <Button
            href={addUTMs(_dashboardUrl + '/register', 'pro_comparison')}
            target="_blank"
            rel="noopener noreferrer"
            magnetic
            size="lg"
            data-ph-capture
            className="bg-zibarit-verde-brillante text-zibarit-negro hover:bg-zibarit-verde-secundario"
            onClick={() => posthog?.capture('landing_cta_clicked', {
              button_text: 'EMPIEZA GRATIS',
              destination_url: addUTMs(_dashboardUrl + '/register', 'pro_comparison'),
              page: 'pro',
            })}
          >
            EMPIEZA GRATIS
          </Button>
        </motion.div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/*  Comparison cell                                                    */
/* ------------------------------------------------------------------ */

function ComparisonCell({
  value,
  isZibarit = false,
  isHighlighted = false,
  isLast = false,
}: {
  value: string;
  isZibarit?: boolean;
  isHighlighted?: boolean;
  isLast?: boolean;
}) {
  const isCheck = value === 'check';
  const isCross = value === 'cross';

  return (
    <div
      className={cn(
        'p-4 flex items-center justify-center',
        !isLast && 'border-r border-white/5',
        isHighlighted && 'bg-zibarit-verde-oscuro/10',
      )}
    >
      {isCheck ? (
        <div
          className={cn(
            'w-7 h-7 rounded-full flex items-center justify-center',
            isZibarit ? 'bg-zibarit-verde-oscuro/30' : 'bg-white/10',
          )}
        >
          <svg className={cn('w-4 h-4', isZibarit ? 'text-zibarit-verde-brillante' : 'text-zibarit-gris')} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
        </div>
      ) : isCross ? (
        <div className="w-7 h-7 rounded-full bg-white/5 flex items-center justify-center">
          <svg className="w-3.5 h-3.5 text-zibarit-gris/40" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </div>
      ) : (
        <span
          className={cn(
            'font-mono text-sm font-medium',
            isZibarit ? 'text-zibarit-bone' : 'text-zibarit-gris',
          )}
        >
          {value}
        </span>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Mobile comparison card                                             */
/* ------------------------------------------------------------------ */

function MobileComparisonCard({
  title,
  accent,
  highlighted = false,
  data,
}: {
  title: string;
  accent: string;
  highlighted?: boolean;
  data: Record<string, string>;
}) {
  return (
    <motion.div
      variants={fadeInUp}
      className={cn(
        'rounded-2xl overflow-hidden',
        highlighted
          ? 'border-2 border-zibarit-verde-oscuro bg-white/[0.06]'
          : 'border border-white/10 bg-white/[0.03]',
      )}
    >
      <div
        className="h-1"
        style={{ backgroundColor: accent }}
      />
      <div className="p-5">
        <h4 className="text-subtitle text-sm text-zibarit-bone mb-4">{title}</h4>
        <div className="space-y-3">
          {Object.entries(data).map(([label, value]) => {
            const isCheck = value === 'check';
            const isCross = value === 'cross';
            return (
              <div key={label} className="flex items-center justify-between">
                <span className="text-body text-sm text-zibarit-marron-secundario">{label}</span>
                {isCheck ? (
                  <svg className="w-5 h-5 text-zibarit-verde-brillante" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                ) : isCross ? (
                  <svg className="w-4 h-4 text-zibarit-gris/40" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                ) : (
                  <span className="font-mono text-sm text-zibarit-bone">{value}</span>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </motion.div>
  );
}

/* ------------------------------------------------------------------ */
/*  Icons                                                              */
/* ------------------------------------------------------------------ */

function CheckIcon({ color }: { color: string }) {
  return (
    <div
      className="w-5 h-5 rounded-full flex items-center justify-center shrink-0 mt-0.5"
      style={{ backgroundColor: `${color}20` }}
    >
      <svg
        className="w-3 h-3"
        style={{ color }}
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth={3}
      >
        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
      </svg>
    </div>
  );
}

function CrossIcon() {
  return (
    <div className="w-5 h-5 rounded-full bg-zibarit-gris/10 flex items-center justify-center shrink-0 mt-0.5">
      <svg className="w-3 h-3 text-zibarit-gris/50" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
      </svg>
    </div>
  );
}

function CommunityIcon() {
  return (
    <svg className="w-5 h-5 text-zibarit-verde-brillante" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-5.058 2.772m0 0a3 3 0 00-4.681 2.72 8.986 8.986 0 003.74.477m.94-3.197a5.971 5.971 0 00-.94 3.197M15 6.75a3 3 0 11-6 0 3 3 0 016 0zm6 3a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zm-13.5 0a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z" />
    </svg>
  );
}

function SectorIcon() {
  return (
    <svg className="w-5 h-5 text-zibarit-verde-brillante" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 3c2.755 0 5.455.232 8.083.678.533.09.917.556.917 1.096v1.044a2.25 2.25 0 01-.659 1.591l-5.432 5.432a2.25 2.25 0 00-.659 1.591v2.927a2.25 2.25 0 01-1.244 2.013L9.75 21v-6.568a2.25 2.25 0 00-.659-1.591L3.659 7.409A2.25 2.25 0 013 5.818V4.774c0-.54.384-1.006.917-1.096A48.32 48.32 0 0112 3z" />
    </svg>
  );
}

function MegaphoneIcon() {
  return (
    <svg className="w-5 h-5 text-zibarit-verde-brillante" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M10.34 15.84c-.688-.06-1.386-.09-2.09-.09H7.5a4.5 4.5 0 110-9h.75c.704 0 1.402-.03 2.09-.09m0 9.18c.253.962.584 1.892.985 2.783.247.55.06 1.21-.463 1.511l-.657.38c-.551.318-1.26.117-1.527-.461a20.845 20.845 0 01-1.44-4.282m3.102.069a18.03 18.03 0 01-.59-4.59c0-1.586.205-3.124.59-4.59m0 9.18a23.848 23.848 0 018.835 2.535M10.34 6.66a23.847 23.847 0 008.835-2.535m0 0A23.74 23.74 0 0018.795 3m.38 1.125a23.91 23.91 0 011.014 5.395m-1.014 8.855c-.118.38-.245.754-.38 1.125m.38-1.125a23.91 23.91 0 001.014-5.395m0-3.46c.495.413.811 1.035.811 1.73 0 .695-.316 1.317-.811 1.73m0-3.46a24.347 24.347 0 010 3.46" />
    </svg>
  );
}

function ShieldIcon() {
  return (
    <svg className="w-5 h-5 text-zibarit-verde-brillante" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
    </svg>
  );
}
